import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import L from 'leaflet';

type CatchLocationMapPreviewProps = {
  lat: number;
  lon: number;
  label?: string | null;
  isDarkMode: boolean;
  onOpen?: () => void;
  actionLabel?: string;
  expandable?: boolean;
};

const BIG_BLUE_TILE = 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png';
const BIG_BLUE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles courtesy of <a href="https://www.hotosm.org/">HOT</a>';
const BIG_BLUE_DARK_FILTER = 'brightness(0.50) contrast(0.96) saturate(0.72)';

const pinIcon = (isDarkMode: boolean, large = false) => {
  const width = large ? 42 : 36;
  const height = large ? 48 : 42;
  const head = large ? 30 : 26;
  const left = Math.round((width - head) / 2);

  return L.divIcon({
    className: 'roddo-catch-location-pin bg-transparent',
    html: `<div style="position:relative;width:${width}px;height:${height}px;filter:drop-shadow(0 7px 10px rgba(2,8,23,.38));"><div style="position:absolute;left:${left}px;top:2px;width:${head}px;height:${head}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${isDarkMode ? '#67D8E8' : '#315D9C'};border:3px solid #fff;box-sizing:border-box;"><span style="position:absolute;left:50%;top:50%;width:${large ? 8 : 7}px;height:${large ? 8 : 7}px;border-radius:999px;background:#fff;transform:translate(-50%,-50%) rotate(45deg);"></span></div></div>`,
    iconSize: [width, height],
    iconAnchor: [Math.round(width / 2), height - 6],
    popupAnchor: [0, -(height - 8)],
  });
};

const createTileLayer = (isDarkMode: boolean) => {
  const layer = L.tileLayer(BIG_BLUE_TILE, {
    attribution: BIG_BLUE_ATTRIBUTION,
    maxZoom: 18,
  });

  layer.on('add', () => {
    window.setTimeout(() => {
      const el = layer.getContainer?.() as HTMLElement | undefined;
      if (el) {
        el.style.filter = 'none';
        el.style.transition = 'none';
      }
      const map = (layer as any)._map;
      const tilePane = map?.getPane?.('tilePane') as HTMLElement | undefined;
      if (tilePane) {
        tilePane.style.filter = isDarkMode ? BIG_BLUE_DARK_FILTER : 'none';
        tilePane.style.transition = 'filter 180ms ease';
        tilePane.style.willChange = isDarkMode ? 'filter' : 'auto';
      }
    }, 0);
  });

  return layer;
};

const MiniMap: React.FC<{
  lat: number;
  lon: number;
  isDarkMode: boolean;
}> = ({ lat, lon, isDarkMode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;

    const map = L.map(ref.current, {
      zoomControl: false,
      attributionControl: true,
      dragging: false,
      touchZoom: false,
      doubleClickZoom: false,
      scrollWheelZoom: false,
      boxZoom: false,
      keyboard: false,
    }).setView([lat, lon], 14);

    mapRef.current = map;
    createTileLayer(isDarkMode).addTo(map);
    markerRef.current = L.marker([lat, lon], { icon: pinIcon(isDarkMode) }).addTo(map);

    const timer = window.setTimeout(() => map.invalidateSize(false), 80);

    return () => {
      window.clearTimeout(timer);
      markerRef.current = null;
      mapRef.current = null;
      try { map.remove(); } catch {}
    };
  }, [isDarkMode]);

  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    marker.setLatLng([lat, lon]);
    map.setView([lat, lon], Math.max(map.getZoom(), 14), { animate: false });
  }, [lat, lon]);

  return <div ref={ref} className="h-full w-full" aria-hidden="true" />;
};

const LargeMapViewer: React.FC<{
  lat: number;
  lon: number;
  label?: string | null;
  isDarkMode: boolean;
  onClose: () => void;
}> = ({ lat, lon, label, isDarkMode, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    if (!ref.current) return;

    const map = L.map(ref.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView([lat, lon], 15);
    createTileLayer(isDarkMode).addTo(map);
    const marker = L.marker([lat, lon], { icon: pinIcon(isDarkMode, true) }).addTo(map);
    if (label?.trim()) marker.bindPopup(label.trim()).openPopup();

    const timer = window.setTimeout(() => map.invalidateSize(false), 100);
    return () => {
      window.clearTimeout(timer);
      try { map.remove(); } catch {}
    };
  }, [lat, lon, label, isDarkMode]);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/72 px-3 py-[76px] backdrop-blur-sm sm:px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Catch location map"
    >
      <div
        className="relative flex h-[min(72dvh,600px)] w-full max-w-[440px] flex-col overflow-hidden rounded-[24px] border shadow-2xl"
        style={{
          background: isDarkMode ? '#0D1322' : '#FFF9F0',
          borderColor: isDarkMode ? 'rgba(255,255,255,.14)' : 'rgba(91,67,49,.18)',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <div className="text-[11px] font-black uppercase tracking-[0.15em] opacity-60">Catch location</div>
            <div className="truncate text-[15px] font-black">{label || `${lat.toFixed(5)}, ${lon.toFixed(5)}`}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border text-lg font-black"
            style={{ borderColor: isDarkMode ? 'rgba(255,255,255,.14)' : 'rgba(91,67,49,.16)' }}
            aria-label="Close map"
          >
            ×
          </button>
        </div>
        <div className="min-h-0 flex-1 px-3 pb-3"><div ref={ref} className="h-full w-full overflow-hidden rounded-[18px] border" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,.10)' : 'rgba(91,67,49,.12)' }} /></div>
      </div>
    </div>,
    document.body,
  );
};

const CatchLocationMapPreview: React.FC<CatchLocationMapPreviewProps> = ({
  lat,
  lon,
  label,
  isDarkMode,
  onOpen,
  actionLabel,
  expandable = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const open = () => {
    if (onOpen) {
      onOpen();
      return;
    }
    if (expandable) setIsExpanded(true);
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
          }
        }}
        className="group relative mt-2 h-[152px] w-full cursor-pointer overflow-hidden rounded-[16px] border focus:outline-none focus-visible:ring-2 focus-visible:ring-[#67E8F9]/60"
        style={{
          borderColor: isDarkMode ? 'rgba(255,255,255,.12)' : 'rgba(91,67,49,.14)',
          background: isDarkMode ? '#121A2B' : 'rgba(248,240,230,.94)',
        }}
        aria-label={actionLabel || (expandable ? 'View larger catch location map' : 'Adjust catch location')}
      >
        <div className="pointer-events-none absolute inset-0">
          <MiniMap lat={lat} lon={lon} isDarkMode={isDarkMode} />
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/70 via-black/25 to-transparent px-3 pb-3 pt-8 text-white">
          <div className="min-w-0">
            <div className="text-[9px] font-black uppercase tracking-[0.14em] text-white/72">Exact catch spot</div>
            <div className="truncate text-[12px] font-black">{label || `${lat.toFixed(5)}, ${lon.toFixed(5)}`}</div>
          </div>
          <div className="shrink-0 rounded-full border border-white/20 bg-black/36 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.11em] backdrop-blur-sm">
            {actionLabel || (expandable ? 'View larger' : 'Adjust spot')}
          </div>
        </div>
      </div>

      {isExpanded ? (
        <LargeMapViewer
          lat={lat}
          lon={lon}
          label={label}
          isDarkMode={isDarkMode}
          onClose={() => setIsExpanded(false)}
        />
      ) : null}
    </>
  );
};

export default CatchLocationMapPreview;
